import Prismic from '@prismicio/client';
import { Link, RichText } from 'prismic-reactjs';
import {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
  NextPage,
} from 'next';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import * as Sentry from '@sentry/nextjs';
import { Document } from '@prismicio/client/types/documents';
import { ParsedUrlQuery } from 'querystring';
import { Ref } from '@prismicio/client/types/ResolvedApi';
import set from 'lodash.set';
import { nanoid } from 'nanoid';
import { getColor } from '../../../../../utils/colors';
import {
  PRISMIC_API_ENDPOINT, SPANISH_LOCALE, PUBLIC_DEFAULT_LOCALE,
} from '../../../../../utils/constants';
import resolver from '../../../../../sm-resolver';
import {
  getMetaText,
  getRealLocale,
  getUidPath,
  getVerticalId,
  prismicLinkResolver,
  sanitize,
} from '../../../../../utils/common';
import useUpdatePreviewRef from '../../../../../hooks/useUpdatePreviewRef';
import LinkWithQuery from '../../../../../components/LinkWithQuery';
import Layout from '../../../../../components/Layout';

interface Props {
  header: Document;
  page: Document;
  footer: Document;
  preview: { activeRef: Ref };
}

type Page = NextPage<Props> & { Layout?: React.FC };

const ThankYouPage: Page = ({
  page, header, footer, preview,
}) => {
  useUpdatePreviewRef(preview, page.id);
  const [firstName, setFirstName] = useState<string>('');
  const [textColor, setTextColor] = useState<string>('');

  useEffect(() => {
    const name = sanitize(localStorage?.getItem('formFirstName'));
    const vertical = sanitize(localStorage?.getItem('formVertical'));

    setFirstName(name);
    setTextColor((getColor(vertical)).text);
  }, []);

  useEffect(() => {
    try {
      setFirstName(sanitize(localStorage.getItem('formFirstName')));

      // clear data
      localStorage.removeItem('formFirstName');
      localStorage.removeItem('formVertical');
    } catch (error) {
      setFirstName('');
      Sentry.captureException(error);
    }
  }, []);

  const metaTitle = getMetaText(page.data?.meta_title, page.lang);
  const metaDescription = getMetaText(page.data?.meta_description, page.lang);
  const metaKeywords = page.data?.meta_keywords?.map(({ word }) => word)?.filter(Boolean) ?? [];

  return (
    <Layout header={header} footer={footer} resolver={resolver}>
      <Head>
        <title>{metaTitle}</title>
        <meta property="og:title" content={metaTitle} key="title" />
        <meta name="description" content={metaDescription} />
        {!!metaKeywords.length && <meta name="keywords" content={metaKeywords.join(', ')} />}
        {metaKeywords.map((word) => <meta key={nanoid()} property="article:tag" content={word} />)}
      </Head>
      <div className="text-center pt-20 pb-16">
        <h3 className="dh-title dark:text-gray-900 uppercase">
          {page.data?.title}
        </h3>
        <p
          className={classNames(
            'mt-5 text-2xl font-semibold uppercase',
            `${textColor}`,
          )}
        >
          {firstName}
        </p>
        <div className="mt-8 leading-7">
          <div className="max-w-xs mx-auto">
            <RichText render={page.data?.description} />
          </div>
        </div>
        <div className={classNames('mt-6 font-semibold text-lg', textColor)}>
          <LinkWithQuery href={Link.url(page.data?.link, prismicLinkResolver)}>
            {page.data?.linkLabel}
          </LinkWithQuery>
        </div>
      </div>
    </Layout>
  );
};

type TPreviewData = GetStaticPropsContext & { previewData? };

export const getStaticProps: GetStaticProps = async ({
  locale,
  params,
  previewData,
}: TPreviewData) => {
  const ref = previewData ? previewData.ref : null;

  const currentLocale = getRealLocale(
    locale || PUBLIC_DEFAULT_LOCALE || SPANISH_LOCALE,
  );

  const client = Prismic.client(PRISMIC_API_ENDPOINT);

  const headerRequest = client.getSingle('header', {
    lang: currentLocale,
    ref,
  });

  const footerRequest = client.getSingle('footer', {
    lang: currentLocale,
    ref,
  });

  const pageRequest = client.getSingle('thankyoupage', {
    lang: currentLocale,
    ref,
  });

  const verticalsRequest = client.query(
    [Prismic.predicates.at('document.type', 'verticalpage')],
    {
      lang: currentLocale,
      ref,
    },
  );

  const clustersRequest = client.query(
    [Prismic.predicates.at('document.type', 'clusterpage')],
    {
      lang: currentLocale,
      ref,
      pageSize: 100,
    },
  );

  const [header, footer, page, verticals, clusters] = await Promise.all(
    [headerRequest, footerRequest, pageRequest, verticalsRequest, clustersRequest],
  );

  const vertical = verticals.results.find((item) => item.uid === params?.verticalid);

  if (page?.data?.slices) {
    page.data.slices = page.data.slices.map((slice) => ({
      ...slice,
      _vertical: vertical,
    }));
  }

  if (!header || !footer || !page) {
    return {
      notFound: true,
    };
  }

  const products = header.data.products.map(
    (item) => set(item, 'link.data.verticalId', getVerticalId(item.link.uid, clusters.results)),
  );

  return {
    props: {
      header: {
        ...header.data,
        products,
        clusters: clusters.results,
        verticals: verticals.results,
      },
      footer: footer.data,
      page,
      preview: {
        activeRef: ref,
      },
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales = [] }) => {
  const client = Prismic.client(PRISMIC_API_ENDPOINT);

  const thankyouPagesRequest = client.query(
    [Prismic.Predicates.at('document.type', 'thankyoupage')],
    {
      lang: '*',
    },
  );

  const productsRequest = client.query(
    [Prismic.Predicates.at('document.type', 'products')],
    {
      lang: '*',
    },
  );

  const clustersRequest = client.query(
    [Prismic.Predicates.at('document.type', 'clusterpage')],
    {
      lang: '*',
      pageSize: 100,
    },
  );

  const verticalRequest = client.query(
    [Prismic.Predicates.at('document.type', 'verticalpage')],
    {
      lang: '*',
    },
  );

  const [thankyouPages, products, clusters, verticals] = await Promise.all(
    [thankyouPagesRequest, productsRequest, clustersRequest, verticalRequest],
  );

  const isSameLanguage = (locale: string, lang?: string): boolean => getRealLocale(locale)
    .toLowerCase() === lang?.toLowerCase();

  const paths: {
    params: ParsedUrlQuery;
    locale?: string | undefined;
  }[] = [];

  locales.forEach((locale) => {
    products.results.forEach(({ uid: productid, data, lang }) => {
      if (
        !isSameLanguage(locale, lang)
        || !productid
        || !data?.clusterPage?.uid
      ) {
        return;
      }

      const uid = getUidPath('products', locale);
      const cluster = clusters.results.find((item) => item.uid === data?.clusterPage?.uid && item.lang === lang);
      const clusterid = data?.clusterPage?.uid;
      const verticalid = cluster?.data.verticalPage.uid;

      thankyouPages.results.forEach(({ uid: thankyouid, lang: thankyouLang }) => {
        if (!isSameLanguage(locale, thankyouLang) || !thankyouid || !verticalid) return;

        paths.push({
          params: {
            uid,
            verticalid,
            clusterid,
            productid,
            thankyouid,
          },
          locale,
        });
      });
    });

    verticals.results.forEach(({ uid: verticalid, lang }) => {
      const thankyouPage = thankyouPages.results.find(({ lang: thankyouLang }) => lang === thankyouLang);
      const uid = getUidPath('products', locale);

      if (thankyouPage?.uid) {
        paths.push({
          params: {
            uid,
            verticalid,
            clusterid: '_',
            productid: '_',
            thankyouid: thankyouPage.uid,
          },
          locale,
        });
      }
    });
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export default ThankYouPage;
